import React from 'react';
import { Helmet } from 'react-helmet-async';
import { REGION, SEO_ROUTES, SeoRoute } from '../config/region';
import { alternatesFor } from '../config/market';

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  type?: string;
  article?: {
    publishedTime?: string;
    author?: string;
    section?: string;
  };
}


const SITE_NAME = REGION.site.name;
const BASE_URL = REGION.site.baseUrl;
const DEFAULT_IMAGE = `${BASE_URL}${REGION.site.image}`;

const ROUTE_SEO: Record<string, SeoRoute> = Object.fromEntries(
  SEO_ROUTES.map((route) => [route.path, route])
);

const HOME = ROUTE_SEO['/'];

/**
 * Resolve a page's tags, preferring what the caller passed and falling back to
 * the market's seo.json (src/config/regions/<code>/) for the route.
 *
 * The fallback is the point: scripts/prerender-routes.js bakes the very same
 * JSON into the static HTML for each URL, so what a crawler reads before running
 * any JavaScript matches what React renders afterwards. Passing title/description
 * inline still works, but it reintroduces two places to change one string.
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = '',
  image,
  type = 'website',
  article,
}) => {
  const route = ROUTE_SEO[path];
  const resolvedDescription = description ?? route?.description ?? HOME.description;

  const explicitFullTitle = !title && route?.fullTitle;
  const resolvedTitle = title ?? route?.title;
  const fullTitle = explicitFullTitle
    ? route.fullTitle!
    : resolvedTitle
      ? `${resolvedTitle} | ${SITE_NAME}`
      : HOME.fullTitle!;

  const url = `${BASE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;
  // The same page in the other markets (hreflang), for the routes seo.json
  // lists; scripts/prerender-routes.js bakes the same links into the HTML.
  // Anything else (a blog post) declares none.
  const alternates = route ? alternatesFor(REGION, route.path) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={url} />
      {alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article-specific */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {article?.section && (
        <meta property="article:section" content={article.section} />
      )}
    </Helmet>
  );
};

export default SEO;
